import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import {
  AttendanceRecord,
  AttendanceStatus,
  AuthorizedPickup,
  CheckedOutBy,
  AttendanceRecordRow,
  AuthorizedPickupRow,
  REAL_SESSION_KEY,
  toAttendanceRecord,
  toAuthorizedPickup,
} from '../types/attendance';
import { analytics } from '../utils/analytics';
import {
  STUDENT_PHOTO_BUCKET,
  pickupPhotoObjectPath,
  toSquareHeadshot,
} from '../utils/studentPhotoImage';
import { isSampleId } from '../staff-app/sample/sampleData';
import {
  getSampleRecords,
  sampleCheckIn,
  sampleClearAbsence,
  sampleGuardians,
  sampleMarkAbsent,
  sampleMarkPickedUp,
} from '../staff-app/sample/sampleStore';

// ── Keeping attendance fresh ──────────────────────────────────────────────────
//
// These two queries poll rather than subscribe. Both used to open a `postgres_changes`
// channel, which never delivered anything: `attendance_records` was not in the
// `supabase_realtime` publication, and `.subscribe()` without a status callback made the
// failure indistinguishable from a quiet table. Publishing the table fixed the server side,
// but the client side then hit a second problem — React StrictMode mounts an effect, tears
// it down, and mounts it again, while supabase-js `removeChannel` disconnects the whole
// socket once its last channel goes. The remount joins a closing socket and the channel
// times out for good.
//
// Surviving that needs deferred teardown or a ref-counted shared channel. It is not worth it
// here: dismissal tolerates tens of seconds, the payload is one small indexed select, and
// polling has no infrastructure to keep working. React Query pauses these timers while the
// tab is in the background (`refetchIntervalInBackground` defaults to false) and
// `refetchOnWindowFocus` catches up on return, so an idle phone in a pocket costs nothing.

/** Teacher/admin roster: two people work one dismissal line and must converge quickly. */
const ROSTER_POLL_MS = 15_000;

/** Parent views: a pickup is worth knowing about promptly, not instantly. */
const PARENT_POLL_MS = 30_000;

// ── Query keys ────────────────────────────────────────────────────────────────

export const attendanceKeys = {
  // `virtualClassKey` sits after `sessionDate` so the prefix `['attendance','programs',
  // sessionDate]` still matches every variant — that prefix is what `invalidateRosterViews`
  // sweeps, and burying the discriminator deeper would leave Chinese rosters stale after a
  // check-in.
  byPrograms: (
    programIds: readonly string[],
    sessionDate: string,
    virtualClassKey: string = REAL_SESSION_KEY,
  ) =>
    ['attendance', 'programs', sessionDate, virtualClassKey, ...[...programIds].sort()] as const,
  byStudents: (studentIds: string[], sessionDate: string) =>
    ['attendance', 'students', sessionDate, ...studentIds.sort()] as const,
  authorizedPickups: (studentId: string) =>
    ['authorized-pickups', studentId] as const,
};

/**
 * Refresh the roster the teacher is looking at.
 *
 * A group's key embeds the sorted list of program ids it covers, so a mutation that knows
 * only its own program id cannot rebuild that key — it invalidates every group query for
 * the day by prefix instead. There is at most a handful, and they are one small select.
 */
function invalidateRosterViews(client: QueryClient, sessionDate: string) {
  void client.invalidateQueries({ queryKey: ['attendance', 'programs', sessionDate] });
}

// ── Sample mode ───────────────────────────────────────────────────────────────
//
// The teacher portal falls back to a made-up class when nothing meets today (see
// `useTodaysPrograms`). Its ids all start with `sample-`, which no real UUID can, so a
// single check at the top of each query and mutation below diverts the whole operation to
// the browser-only store — no request is issued, and nothing is ever written to the
// database. Every other caller is untouched.

// ── Teacher: roster across several programs on a given date ──────────────────

/**
 * Every attendance record for a group of programs a teacher works as one line — BHCS's
 * four after-school programs all dismiss together, so their check-ins have to arrive and
 * refresh together too.
 *
 * One query for the whole group: a query per program would mean four refetches, and four
 * poll timers, for a single check-in.
 *
 * `virtualClassKey` says which layer of the day is being asked for, and is not optional in
 * spirit even though it defaults. A virtual class writes its attendance against the child's
 * *after-school* `program_id`, so the four after-school ids match the Chinese and Math rows
 * just as readily as the real ones — without this filter the After School roster would show
 * a child as checked in because their Chinese teacher checked them in an hour earlier.
 */
export function useAttendanceByPrograms(
  programIds: readonly string[],
  sessionDate: string,
  virtualClassKey: string = REAL_SESSION_KEY,
) {
  const ids = [...programIds].sort();
  const realIds = ids.filter(id => !isSampleId(id));
  const sampleIds = ids.filter(isSampleId);
  const enabled = ids.length > 0 && Boolean(sessionDate);

  const query = useQuery<AttendanceRecord[]>({
    queryKey: attendanceKeys.byPrograms(ids, sessionDate, virtualClassKey),
    queryFn: async () => {
      const sampleRecords = sampleIds.flatMap(id => getSampleRecords(id, sessionDate));
      if (realIds.length === 0) return sampleRecords;

      const { data, error } = await supabase
        .from('attendance_records')
        .select('*')
        .in('program_id', realIds)
        .eq('session_date', sessionDate)
        .eq('virtual_class_key', virtualClassKey);
      if (error) throw error;
      return [...sampleRecords, ...(data as AttendanceRecordRow[]).map(toAttendanceRecord)];
    },
    enabled,
    // A sample day exists only in this browser, so nothing off-device can change it.
    refetchInterval: realIds.length > 0 ? ROSTER_POLL_MS : false,
  });

  return query;
}

// ── Parent: records for a list of students on a given date ────────────────────

/**
 * Every record for these students today, with the program's type alongside.
 *
 * The type is embedded rather than joined in the caller because both parent surfaces need
 * the same thing from it — whether this was a pickup-line handover or an enrichment class
 * ending — and neither has the program in hand otherwise. Parents can read `programs`
 * ("Everyone can view programs"), so this costs no extra round trip and no policy change.
 *
 * Virtual classes are excluded twice over. RLS already makes them unreadable for a parent,
 * which is the guarantee that matters; the filter here is the second layer, and the reason
 * it cannot be dropped as redundant is that the embedded `programs(type)` on a virtual row
 * says `'after-school'` — so every downstream `isPickupProgram()` check would wave it
 * through as a genuine handover.
 */
export function useAttendanceByStudents(studentIds: string[], sessionDate: string) {
  const enabled = studentIds.length > 0 && Boolean(sessionDate);

  return useQuery<AttendanceRecord[]>({
    queryKey: attendanceKeys.byStudents(studentIds, sessionDate),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('attendance_records')
        .select('*, programs(type)')
        .in('student_id', studentIds)
        .eq('session_date', sessionDate)
        .eq('virtual_class_key', REAL_SESSION_KEY);
      if (error) throw error;
      return (data as AttendanceRecordRow[]).map(toAttendanceRecord);
    },
    enabled,
    refetchInterval: PARENT_POLL_MS,
  });
}

// ── Teacher: bulk upsert 'not_arrived' for a roster ──────────────────────────

interface RosterEntry {
  studentId: string;
  programId: string;
  enrollmentId: string;
  sessionDate: string;
  /**
   * Which class this row is being seeded for. Omitted — meaning `''` — for a real session.
   *
   * A virtual class passes its key here while still sending the child's after-school
   * `programId` and `enrollmentId`, which is what makes the three rows a child produces on
   * one day distinct rather than one row overwritten twice.
   */
  virtualClassKey?: string;
}

export function useUpsertRoster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entries: RosterEntry[]) => {
      if (entries.length === 0) return;
      // The sample day is seeded whole, so there is nothing to backfill.
      if (isSampleId(entries[0].programId)) return;
      const rows = entries.map(e => ({
        student_id: e.studentId,
        program_id: e.programId,
        enrollment_id: e.enrollmentId,
        session_date: e.sessionDate,
        status: 'not_arrived' as const,
        virtual_class_key: e.virtualClassKey ?? REAL_SESSION_KEY,
      }));
      const { error } = await supabase
        .from('attendance_records')
        .upsert(rows, {
          // Must name all four columns of the widened unique key. Naming only the original
          // three would match no constraint and turn every reopen of a roster into an
          // insert conflict rather than a no-op.
          onConflict: 'enrollment_id,program_id,session_date,virtual_class_key',
          ignoreDuplicates: true,
        });
      if (error) throw error;
    },
    onSuccess: (_data, entries) => {
      if (entries.length === 0) return;
      invalidateRosterViews(queryClient, entries[0].sessionDate);
    },
  });
}

// ── Teacher: mark student as checked in ──────────────────────────────────────

export function useTeacherCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recordId,
      programId,
      sessionDate,
      teacherName,
      studentId,
      previousStatus,
      previousAbsenceReason,
    }: {
      recordId: string;
      programId: string;
      sessionDate: string;
      teacherName?: string | null;
      studentId?: string;
      /** What the row said before, so a check-in over an absence can be logged as such. */
      previousStatus?: AttendanceStatus;
      previousAbsenceReason?: string | null;
    }) => {
      if (isSampleId(recordId)) {
        sampleCheckIn(recordId, teacherName);
        return { programId, sessionDate };
      }
      const { data: auth } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('attendance_records')
        .update({
          status: 'checked_in',
          checked_in_at: new Date().toISOString(),
          checked_in_by: 'teacher',
          checked_in_by_user_id: auth.user?.id ?? null,
          // Name is snapshotted, not looked up later, so the audit trail survives a staff
          // rename or removal.
          checked_in_by_name: teacherName ?? null,
          // Checking a student in retracts any earlier absence, so its reason must go too.
          absence_reason: null,
        })
        .eq('id', recordId)
        .in('status', ['not_arrived', 'absent'])
        .select('id');
      if (error) throw error;

      // A late arrival silently retracts an absence. Log it, or the trail shows absences
      // that were set and never released. Only when a row actually matched: the status
      // guard above can reject, and an audit event for a write that did not happen is worse
      // than none. A check-in over `not_arrived` retracts nothing, so it logs nothing.
      if (previousStatus === 'absent' && (data?.length ?? 0) > 0 && studentId) {
        analytics.trackAbsenceCleared({
          recordId,
          studentId,
          programId,
          sessionDate,
          actorName: teacherName,
          previousReason: previousAbsenceReason ?? null,
          clearedVia: 'check_in',
        });
      }
      return { programId, sessionDate };
    },
    onSuccess: (result) => {
      invalidateRosterViews(queryClient, result.sessionDate);
    },
  });
}

export function useMarkAbsent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recordId,
      programId,
      sessionDate,
      reason,
      studentId,
      teacherName,
      previousStatus,
    }: {
      recordId: string;
      programId: string;
      sessionDate: string;
      reason?: string | null;
      studentId?: string;
      teacherName?: string | null;
      previousStatus?: AttendanceStatus;
    }) => {
      if (isSampleId(recordId)) {
        sampleMarkAbsent(recordId, reason);
        return { programId, sessionDate };
      }
      const { data, error } = await supabase
        .from('attendance_records')
        .update({
          status: 'absent',
          absence_reason: reason ?? null,
          // A correction after a mistaken check-in must not leave the check-in stamps
          // behind, or the row would claim the student both arrived and never came.
          checked_in_at: null,
          checked_in_by: null,
          checked_in_by_user_id: null,
          checked_in_by_name: null,
        })
        .eq('id', recordId)
        // Includes 'checked_in' so a mis-tap can be corrected. 'picked_up' is excluded:
        // a student who was collected demonstrably attended.
        .in('status', ['not_arrived', 'checked_in'])
        .select('id');
      if (error) throw error;

      if ((data?.length ?? 0) > 0 && studentId) {
        analytics.trackAbsenceSet({
          recordId,
          studentId,
          programId,
          sessionDate,
          actorName: teacherName,
          reason: reason ?? null,
          previousStatus: previousStatus ?? 'not_arrived',
        });
      }
      return { programId, sessionDate };
    },
    onSuccess: (result) => {
      invalidateRosterViews(queryClient, result.sessionDate);
    },
  });
}

// ── Teacher: take an absence back ─────────────────────────────────────────────

/**
 * Undo an absence, returning the student to "not checked in" and dropping the reason.
 *
 * Separate from a late check-in on purpose. Checking a student in also clears the absence,
 * but it asserts they arrived; this asserts only that the absence was wrong — the teacher
 * tapped the wrong row, or picked the wrong reason. Collapsing the two would put a check-in
 * on the record for a child who may never have come.
 *
 * Guarded to `absent` so an undo cannot walk back a check-in or a completed pickup: by the
 * time either of those lands the row has moved on, and this becomes a no-op.
 */
export function useClearAbsence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recordId,
      programId,
      sessionDate,
      studentId,
      teacherName,
      previousReason,
    }: {
      recordId: string;
      programId: string;
      sessionDate: string;
      studentId?: string;
      teacherName?: string | null;
      /** The reason being dropped, kept for the audit trail before it is erased. */
      previousReason?: string | null;
    }) => {
      if (isSampleId(recordId)) {
        sampleClearAbsence(recordId);
        return { programId, sessionDate };
      }
      const { data, error } = await supabase
        .from('attendance_records')
        .update({
          status: 'not_arrived',
          absence_reason: null,
          // `useMarkAbsent` already nulls these, but rows written before that guard existed
          // can still carry check-in stamps under an absent status. Clearing them here means
          // the undo always lands on a genuinely blank row.
          checked_in_at: null,
          checked_in_by: null,
          checked_in_by_user_id: null,
          checked_in_by_name: null,
        })
        .eq('id', recordId)
        .eq('status', 'absent')
        .select('id');
      if (error) throw error;

      if ((data?.length ?? 0) > 0 && studentId) {
        analytics.trackAbsenceCleared({
          recordId,
          studentId,
          programId,
          sessionDate,
          actorName: teacherName,
          previousReason: previousReason ?? null,
          clearedVia: 'undo',
        });
      }
      return { programId, sessionDate };
    },
    onSuccess: (result) => {
      invalidateRosterViews(queryClient, result.sessionDate);
    },
  });
}

// ── Teacher: whole-class shortcuts ───────────────────────────────────────────
//
// A class of twenty ending together is one event, not twenty. These do it in a single
// round trip rather than firing the per-student mutation in a loop, which would be twenty
// requests and twenty cache invalidations for one action a teacher thinks of as one press.
//
// Both are deliberately status-guarded in the same statement that writes, so a row that
// moved on between the roster rendering and the button being pressed is skipped by the
// database rather than by a stale list in the browser.

interface BulkVars {
  /** Records to act on. The caller filters to what is actionable and expected today. */
  recordIds: string[];
  sessionDate: string;
  teacherName?: string | null;
}

/**
 * Check in everyone still marked `not_arrived`.
 *
 * `absent` is deliberately NOT included. A teacher who marked a child absent said
 * something specific, and a bulk press meaning "everyone is here" should not quietly
 * retract it — the individual Check In, which logs the retraction, is the way to do that.
 */
export function useBulkCheckIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ recordIds, sessionDate, teacherName }: BulkVars) => {
      if (recordIds.length === 0) return { sessionDate, count: 0 };
      const { data: auth } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('attendance_records')
        .update({
          status: 'checked_in',
          checked_in_at: new Date().toISOString(),
          checked_in_by: 'teacher',
          checked_in_by_user_id: auth.user?.id ?? null,
          checked_in_by_name: teacherName ?? null,
        })
        .in('id', recordIds)
        .eq('status', 'not_arrived')
        .select('id');
      if (error) throw error;
      return { sessionDate, count: data?.length ?? 0 };
    },
    onSuccess: result => invalidateRosterViews(queryClient, result.sessionDate),
  });
}

/**
 * End the session for everyone currently checked in.
 *
 * Class mode only, and the shape of the write says so: `picked_up_by_name` and
 * `pickup_is_authorized` stay null, because nobody collected these children. There is no
 * bulk equivalent for a pickup line — that is twenty separate handovers to twenty named
 * adults, and asserting them in one press would be a lie.
 *
 * Guarded to `checked_in`: a child who never arrived must not be recorded as having left.
 */
export function useBulkClassCheckOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ recordIds, sessionDate, teacherName }: BulkVars) => {
      if (recordIds.length === 0) return { sessionDate, count: 0 };
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase
        .from('attendance_records')
        .update({
          status: 'picked_up',
          picked_up_at: new Date().toISOString(),
          picked_up_by_name: null,
          pickup_is_authorized: null,
          pickup_recorded_by_user_id: session?.user.id ?? null,
          pickup_recorded_by_name: teacherName ?? null,
          checked_out_by: 'teacher',
        })
        .in('id', recordIds)
        .eq('status', 'checked_in')
        .select('id');
      if (error) throw error;
      return { sessionDate, count: data?.length ?? 0 };
    },
    onSuccess: result => invalidateRosterViews(queryClient, result.sessionDate),
  });
}

// ── Teacher: mark student as checked out ─────────────────────────────────────

interface MarkPickedUpCommon {
  recordId: string;
  programId: string;
  sessionDate: string;
  /** The staff member releasing the child — a different question from who collected them. */
  recordedByName?: string | null;
  studentId?: string;
  studentName?: string | null;
}

/** A pickup-line handover: a named adult collects the child. */
interface MarkPickedUpRelease extends MarkPickedUpCommon {
  mode?: 'pickup';
  /** The adult who collected the child. */
  pickedUpByName: string;
  isAuthorized: boolean;
}

/**
 * An enrichment class ending. Nobody collects the child — they go back to after-school or
 * out to the yard they came from — so there is no collector to name and no authorization to
 * judge. The teacher who ended the session is still recorded.
 */
interface MarkPickedUpClassEnd extends MarkPickedUpCommon {
  mode: 'class';
}

export function useMarkPickedUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: MarkPickedUpRelease | MarkPickedUpClassEnd) => {
      const { recordId, programId, sessionDate, recordedByName, studentId, studentName } = vars;
      const isClassEnd = vars.mode === 'class';

      // Both null in class mode, and deliberately so. `pickup_is_authorized = false` is not
      // "no adult was involved" — it is the specific claim that an *unlisted* adult took the
      // child, which is what raises the amber flag on the roster and files an audit event.
      // A chess class finishing must assert neither.
      const pickedUpByName = isClassEnd ? null : vars.pickedUpByName;
      const isAuthorized = isClassEnd ? null : vars.isAuthorized;

      if (isSampleId(recordId)) {
        sampleMarkPickedUp(recordId, { pickedUpByName, isAuthorized, recordedByName });
        return { programId, sessionDate };
      }
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase
        .from('attendance_records')
        .update({
          status: 'picked_up',
          picked_up_at: new Date().toISOString(),
          picked_up_by_name: pickedUpByName,
          pickup_is_authorized: isAuthorized,
          pickup_recorded_by_user_id: session?.user.id ?? null,
          // Snapshotted, not looked up later, so the audit trail survives staff renames.
          pickup_recorded_by_name: recordedByName ?? null,
          checked_out_by: 'teacher',
        })
        .eq('id', recordId)
        .select('id');
      if (error) throw error;

      // Exceptions only. A routine check-out adds nothing to the columns written just above,
      // which is what the admin pickup view reads — and logging every one of them would put a
      // row per child per session (~100/day) on the busiest table we have. An unlisted adult
      // is the one thing the row cannot answer across sessions, and it is a handful a week.
      //
      // Guarded on a matched row as well: RLS can reject silently, and an audit event for a
      // write that did not happen is worse than none. Same rule as the absence events.
      if (isAuthorized === false && (data?.length ?? 0) > 0 && studentId) {
        analytics.trackUnauthorizedPickup({
          recordId,
          studentId,
          studentName,
          programId,
          sessionDate,
          pickedUpByName: pickedUpByName ?? '',
          isAuthorized: false,
          actorName: recordedByName,
        });
      }
      return { programId, sessionDate };
    },
    onSuccess: (result) => {
      invalidateRosterViews(queryClient, result.sessionDate);
    },
  });
}

// The pickup banner was removed: it interrupted every page for every family whose child had
// been collected, to say something the dashboard's Today card now states in place, without a
// dismissal to chase. Its `useDismissPickup` mutation went with it. As with the parent check-in
// below, the `dismiss_pickup_banner` RPC and `attendance_records.parent_dismissed_at` are left
// in the database — dropping them is a separate, riskier change than deleting the caller, and
// the column is still read back by `toAttendanceRecord`.

// Parent-side check-in was removed: children walk over from school and staff record the
// arrival, so the teacher roster is the only writer. The `parent_checkin_student` RPC is
// left in the database — dropping it is a separate, riskier change than deleting its caller.

// ── Parent: self check-out at pickup ─────────────────────────────────────────
//
// The other half of the day, and the opposite call from check-in. Staff record the arrival
// because the children walk over from school with no parent present; at the end of the day the
// parent is the one standing at the door, phone in hand, so the adult collecting the child
// records it. Both writes go through SECURITY DEFINER functions that check everything the page
// does and more — parents have no write policy on `attendance_records` at all.

/** One of the caller's children's after-school records for today, as the pickup screen reads it. */
export interface PickupTodayRecord {
  recordId: string;
  studentId: string;
  studentName: string;
  programName: string;
  status: AttendanceStatus;
  checkedInAt: string | null;
  pickedUpAt: string | null;
  pickedUpByName: string | null;
  checkedOutBy: CheckedOutBy | null;
}

interface PickupTodayRow {
  record_id: string;
  student_id: string;
  student_name: string;
  program_name: string;
  status: AttendanceStatus;
  checked_in_at: string | null;
  picked_up_at: string | null;
  picked_up_by_name: string | null;
  checked_out_by: CheckedOutBy | null;
}

const pickupTodayKey = (userId: string | null | undefined) =>
  ['attendance', 'pickup-today', userId ?? 'anonymous'] as const;

/**
 * Today's after-school records for the signed-in parent's children.
 *
 * Today is the database's today in the business timezone, not the phone's: a parent's clock is
 * not something a check-out should depend on, and the function that writes uses the same one.
 * Polls like the other parent views, so a sibling the other parent just collected stops being
 * offered without a reload.
 */
export function useParentPickupToday(userId: string | null | undefined) {
  return useQuery<PickupTodayRecord[]>({
    queryKey: pickupTodayKey(userId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('parent_pickup_today');
      if (error) throw error;
      return ((data ?? []) as PickupTodayRow[]).map(row => ({
        recordId: row.record_id,
        studentId: row.student_id,
        studentName: row.student_name,
        programName: row.program_name,
        status: row.status,
        checkedInAt: row.checked_in_at,
        pickedUpAt: row.picked_up_at,
        pickedUpByName: row.picked_up_by_name,
        checkedOutBy: row.checked_out_by,
      }));
    },
    enabled: Boolean(userId),
    refetchInterval: PARENT_POLL_MS,
  });
}

/** How the parent reached the pickup screen — the QR code at the door, or the app itself. */
export type PickupEntry = 'qr' | 'app';

interface ParentCheckOutVars {
  records: Pick<PickupTodayRecord, 'recordId' | 'studentId' | 'studentName'>[];
  via: PickupEntry;
}

export interface ParentCheckOutResult {
  /** The records the database actually closed, with the time it wrote. */
  closed: { recordId: string; pickedUpAt: string }[];
}

/**
 * Check the selected children out, naming the signed-in parent as the adult who took them.
 *
 * The database skips any record that is no longer checked in rather than failing the batch —
 * the other parent may have collected a sibling a few seconds earlier — so the result says
 * which ones closed and the screen reports exactly those.
 */
export function useParentCheckOut(userId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ records, via }: ParentCheckOutVars): Promise<ParentCheckOutResult> => {
      if (records.length === 0) return { closed: [] };
      const { data, error } = await supabase.rpc('parent_checkout_students', {
        p_record_ids: records.map(r => r.recordId),
      });
      if (error) throw error;
      const closed = ((data ?? []) as { record_id: string; picked_up_at: string }[])
        .map(row => ({ recordId: row.record_id, pickedUpAt: row.picked_up_at }));

      // One event per child, so adoption reads per session the way the roster does, and
      // `via` says which entrance families actually use.
      const byId = new Map(records.map(r => [r.recordId, r]));
      for (const { recordId } of closed) {
        const record = byId.get(recordId);
        analytics.trackParentCheckOut({
          recordId,
          studentId: record?.studentId ?? null,
          studentName: record?.studentName ?? null,
          via,
        });
      }
      return { closed };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pickupTodayKey(userId) });
      // The dashboard's Today card and the Students page read the same records.
      void queryClient.invalidateQueries({ queryKey: ['attendance', 'students'] });
    },
  });
}

// ── Teacher: undo a parent's self check-out ──────────────────────────────────

interface UndoParentCheckOutVars {
  recordId: string;
  programId: string;
  sessionDate: string;
  studentId: string;
  studentName?: string | null;
  teacherName?: string | null;
}

/**
 * Put a child a parent checked out back to checked in.
 *
 * Staff check-outs stay one-way — nothing walks those back — but a parent's tap is not a
 * handover anyone witnessed. A parent who pressed it from the car park, or for the wrong
 * child, leaves a child in the room the roster calls gone, and the teacher looking at that
 * child is the only one who can say so. Guarded to parent check-outs in the write itself.
 */
export function useUndoParentCheckOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: UndoParentCheckOutVars) => {
      const { recordId, programId, sessionDate, studentId, studentName, teacherName } = vars;
      const { data, error } = await supabase
        .from('attendance_records')
        .update({
          status: 'checked_in',
          picked_up_at: null,
          picked_up_by_name: null,
          pickup_is_authorized: null,
          pickup_recorded_by_user_id: null,
          pickup_recorded_by_name: null,
          checked_out_by: null,
        })
        .eq('id', recordId)
        .eq('status', 'picked_up')
        .eq('checked_out_by', 'parent')
        .select('id');
      if (error) throw error;
      if ((data?.length ?? 0) > 0) {
        analytics.trackParentCheckOutUndone({
          recordId,
          studentId,
          studentName,
          programId,
          sessionDate,
          actorName: teacherName,
        });
      }
      return { programId, sessionDate };
    },
    onSuccess: result => invalidateRosterViews(queryClient, result.sessionDate),
  });
}

// ── Authorized pickups ────────────────────────────────────────────────────────

export function useAuthorizedPickups(studentId: string) {
  return useQuery<AuthorizedPickup[]>({
    queryKey: attendanceKeys.authorizedPickups(studentId),
    queryFn: async () => {
      if (isSampleId(studentId)) {
        return sampleGuardians(studentId).authorizedPickups.map((p, i) => ({
          id: `sample-pickup-${studentId}-${i}`,
          studentId,
          name: p.name,
          relationship: p.relationship,
          phone: null,
          photoPath: null,
          createdAt: new Date().toISOString(),
        }));
      }
      const { data, error } = await supabase
        .from('student_authorized_pickups')
        .select('*')
        .eq('student_id', studentId)
        .order('name');
      if (error) throw error;
      return (data as AuthorizedPickupRow[]).map(toAuthorizedPickup);
    },
    enabled: Boolean(studentId),
  });
}

export function useAddAuthorizedPickup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      studentId,
      name,
      relationship,
      phone,
      studentName,
      actorType,
    }: {
      studentId: string;
      name: string;
      relationship: string;
      phone: string;
      studentName?: string | null;
      /** Defaults to the parent portal, which was this list's only writer originally. */
      actorType?: 'parent' | 'admin';
    }) => {
      const { error } = await supabase.from('student_authorized_pickups').insert({
        student_id: studentId,
        name: name.trim(),
        relationship: relationship.trim() || null,
        phone: phone.trim() || null,
      });
      if (error) throw error;

      analytics.trackAuthorizedPickupAdd({
        studentId,
        studentName,
        name: name.trim(),
        relationship: relationship.trim(),
        phone: phone.trim(),
        actorType,
      });
    },
    onSuccess: (_data, { studentId }) => {
      void queryClient.invalidateQueries({ queryKey: attendanceKeys.authorizedPickups(studentId) });
    },
  });
}

export function useRemoveAuthorizedPickup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      studentId,
      name,
      relationship,
      phone,
      photoPath,
      studentName,
      actorType,
    }: {
      id: string;
      studentId: string;
      /**
       * The row's details, passed in by the caller that is displaying them. The delete is a
       * hard delete, so this is the last moment they exist anywhere.
       */
      name?: string;
      relationship?: string | null;
      phone?: string | null;
      photoPath?: string | null;
      studentName?: string | null;
      actorType?: 'parent' | 'admin';
    }) => {
      const { data, error } = await supabase
        .from('student_authorized_pickups')
        .delete()
        .eq('id', id)
        .select('id');
      if (error) throw error;

      if ((data?.length ?? 0) > 0) {
        // The row is gone, so nothing points at the object any more. Best effort on purpose:
        // failing here would report the contact as still authorized when they are not, and a
        // stranded 50 KB object is the cheaper of the two wrong answers.
        if (photoPath) {
          await supabase.storage.from(STUDENT_PHOTO_BUCKET).remove([photoPath]);
        }

        analytics.trackAuthorizedPickupRemove({
          studentId,
          studentName,
          pickupId: id,
          name: name ?? '',
          relationship,
          phone,
          actorType,
        });
      }
      return { studentId };
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: attendanceKeys.authorizedPickups(result.studentId),
      });
    },
  });
}

/**
 * Attach a photo to an authorized pickup contact, or clear the one they have.
 *
 * Upload, then repoint, then clean up — the same order as the student headshot importer, and
 * for the same reason: if any step fails the contact keeps whatever photo they already had.
 * The new object is removed again on a failed write, and the old object is only deleted once
 * the row is definitely pointing somewhere else.
 *
 * Pass `file: null` to clear. Clearing repoints the row first and deletes afterwards, so a
 * failed delete leaves an orphaned object rather than a row pointing at nothing.
 */
export function useSetAuthorizedPickupPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      studentId,
      file,
      previousPath,
      name,
      relationship,
      studentName,
      actorType = 'admin',
    }: {
      id: string;
      /** The owning student — it is the leading path segment, and thus the access rule. */
      studentId: string;
      /** The image the admin chose, or null to clear the existing photo. */
      file: File | null;
      previousPath?: string | null;
      name?: string;
      relationship?: string | null;
      studentName?: string | null;
      actorType?: 'parent' | 'admin';
    }) => {
      let nextPath: string | null = null;

      if (file) {
        // Cropped and re-encoded in the browser, so the original phone photo never leaves the
        // admin's machine and the bucket only holds the small square WebP.
        const blob = await toSquareHeadshot(file);
        nextPath = pickupPhotoObjectPath(studentId);
        const { error: uploadError } = await supabase.storage
          .from(STUDENT_PHOTO_BUCKET)
          .upload(nextPath, blob, { contentType: 'image/webp', cacheControl: '3600' });
        if (uploadError) throw uploadError;
      }

      const { error: dbError } = await supabase
        .from('student_authorized_pickups')
        .update({ photo_path: nextPath })
        .eq('id', id);
      if (dbError) {
        if (nextPath) await supabase.storage.from(STUDENT_PHOTO_BUCKET).remove([nextPath]);
        throw dbError;
      }

      // Best effort: an orphaned object costs a few kilobytes, and failing the mutation here
      // would wrongly suggest the new photo did not take.
      if (previousPath && previousPath !== nextPath) {
        await supabase.storage.from(STUDENT_PHOTO_BUCKET).remove([previousPath]);
      }

      analytics.trackAuthorizedPickupPhoto({
        studentId,
        studentName,
        pickupId: id,
        name: name ?? '',
        relationship,
        actorType,
        cleared: !file,
      });

      return { studentId, photoPath: nextPath };
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: attendanceKeys.authorizedPickups(result.studentId),
      });
    },
  });
}
